import { Map, PlayArrow, Stop } from '@mui/icons-material'
import AlarmIcon from '@mui/icons-material/Alarm'
import {
  Alert,
  AlertTitle,
  BottomNavigation,
  BottomNavigationAction,
  Box,
  Button,
  Paper,
  SpeedDial,
  SpeedDialAction,
  SpeedDialIcon,
  Typography,
} from '@mui/material'
import CustomBackdrop from 'common/components/CustomBackdrop'
import CustomClock from 'common/components/CustomClock'
import { CustomModalContext } from 'common/contexts/CustomModalContext'
import useCurrentMenu from 'common/hooks/useCurrentMenu'
import { ModalTypes } from 'common/hooks/useCustomModal'
import { CustomThemeContext } from 'common/providers/CustomThemeProvider'
import useFetchTipoSalida from 'features/control_presencia/hooks/useFetchTipoSalida'
import { enqueueSnackbar } from 'notistack'
import { useContext, useState } from 'react'
import ThemeManager from 'theming/ThemeManager'
import CustomMap from '../components/CustomMap'
import LastFichajesTable from '../components/LastFichajesTable'
import PresenceActions from '../components/PresenceActions'
import useFetchLastFichajes from '../hooks/useFetchLastFichajes'
import FichajesController, { PresenceControlActions } from '../libs/FichajesController'
import { tiposSalidaMap } from '../libs/tiposSalidaMap'

/**
 * @module ControlPresencia
 */

/**
 * This component works as a page for the Presence Control
 * to mark enters and leave
 * @example
 *
 * <Fichajes />
 *
 * @returns {JSX.Element}
 */
const Fichajes = () => {
  /**
   * The tipes of modal
   * @readonly
   * @enum {string}
   */
  const MovilView = {
    Map: 'Map',
    Records: 'Records',
  }
  const menu = useCurrentMenu()
  const { response: tiposEntradaSalida } = useFetchTipoSalida()
  const [lastIteration, setLastIteration] = useState(0)
  const { response, loading, error } = useFetchLastFichajes(lastIteration)
  const { handleModal, modal } = useContext(CustomModalContext)
  const [mobileSection, setMobileSection] = useState(MovilView.Map)
  const [openBackdrop, setOpenBackdrop] = useState(false)
  const [backdropText, setBackdropText] = useState('Guardando Fichaje')
  const [punchIn, setPunchIn] = useState(null)

  const themeManager = new ThemeManager()
  const { themeController } = useContext(CustomThemeContext)

  const PresenceActionsComp =
    themeManager.activeTheme?.components?.PresenceActions ?? PresenceActions

  const LastFichajesTableComp =
    themeManager.activeTheme?.components?.LastFichajesTable ?? LastFichajesTable

  const CustomMapComp = themeManager.activeTheme?.components?.CustomMap ?? CustomMap

  const vistaFuncional = menu?.currentMenu?.parametros?.VISTA_FUNCIONAL === 'S'

  /**
   * Mark a presence control registry
   * @async
   * @param {PresenceControlActions} type - The tipe of action to perform
   * @param {string} [exitCode] - If is a exit type this param must be provided
   * @returns {Promise<void>}
   * */
  async function markAction(type, exitCode) {
    const card = null
    const terminal = menu?.actualMatch?.parametros?.TERMINAL || null
    try {
      setBackdropText('Guardando Fichaje')
      setOpenBackdrop(true)
      await FichajesController.create(type, exitCode, card, terminal)
      setLastIteration(lastIteration + 1)
      enqueueSnackbar('Guardado correctamente', {
        variant: 'success',
      })
    } catch {
      enqueueSnackbar('Ha ocurrido un error y no se ha podido registrar el fichaje', {
        variant: 'error',
      })
    } finally {
      setOpenBackdrop(false)
    }
  }

  async function deleteAction(id_fichaje) {
    try {
      handleModal({ enabled: false })
      setBackdropText('Borrando Fichaje')
      setOpenBackdrop(true)
      await FichajesController.delete(id_fichaje ?? punchIn)
      enqueueSnackbar('Eliminado correctamente', {
        variant: 'success',
      })
    } catch {
      enqueueSnackbar('Ha ocurrido un error y no se ha podido eliminar el fichaje', {
        variant: 'error',
      })
    } finally {
      setLastIteration(lastIteration + 1)
      setOpenBackdrop(false)
    }
  }

  const cancelDelete = () => {
    handleModal({ enabled: false })
  }

  const handleDelete = (e) => {
    if (!e.row.can_delete) return null

    setPunchIn(e.row.id_fichaje)

    handleModal({
      type: ModalTypes.Custom,
      enabled: true,
      content: (
        <Paper sx={{ p: 4 }}>
          <Typography variant="h6">¿Está seguro que quiere borrar el fichaje?</Typography>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant="contained"
              color="success"
              size="large"
              onClick={() => deleteAction(e.row.id_fichaje)}
              sx={{ mt: 2 }}
              fullWidth
            >
              Sí
            </Button>
            <Button
              variant="contained"
              color="error"
              size="large"
              onClick={cancelDelete}
              sx={{ mt: 2 }}
              fullWidth
            >
              No
            </Button>
          </Box>
        </Paper>
      ),
    })
  }

  return (
    <>
      <CustomBackdrop open={openBackdrop} text={backdropText} />
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, height: '100%' }}>
        {themeController?.hide?.Fichajes?.Reloj ? (
          <></>
        ) : (
          <CustomClock showDate={true} timeTextVariant="h5" />
        )}
        <Box
          sx={{
            flexGrow: 0,
            display: {
              xs: vistaFuncional ? 'flex' : 'none',
              sm: 'flex',
            },
          }}
        >
          <PresenceActionsComp
            loading={loading}
            setLastIteration={setLastIteration}
            lastIteration={lastIteration}
            handleModal={handleModal}
            modal={modal}
          />
        </Box>
        <Box
          sx={{
            flexGrow: 1,
            display: 'flex',
            flexDirection: { xs: vistaFuncional ? 'column' : 'row', sm: 'row' },
            gap: 2,
            width: '100%',
          }}
        >
          {error ? (
            <Alert severity="error">
              <AlertTitle>Error de conexión</AlertTitle>
              No se han podido obtener los fichajes anteriores
            </Alert>
          ) : (
            <></>
          )}
          <LastFichajesTableComp
            response={response}
            sx={{
              display: {
                sm: 'initial',
                xs: vistaFuncional
                  ? 'initial'
                  : mobileSection === MovilView.Records
                    ? 'initial'
                    : 'none',
              },
              flexGrow: '1 1 0',
              width: { sm: '50%', md: '30%' },
            }}
            loading={loading}
            onClick={handleDelete}
          />
          <Box
            sx={{
              display: {
                sm: 'initial',
                xs: vistaFuncional
                  ? 'initial'
                  : mobileSection === MovilView.Map
                    ? 'initial'
                    : 'none',
              },
              flex: '1 1 0',
              width: { sm: '50%', md: '30%' },
              '&  .map': {
                // width: "100%",
                height: '100%',
                zIndex: 2,
              },
            }}
          >
            <CustomMapComp />
          </Box>
        </Box>
      </Box>
      <BottomNavigation
        onChange={(event, value) => {
          setMobileSection(value)
        }}
        value={mobileSection}
        sx={{
          display: {
            xs: vistaFuncional ? 'none' : 'flex',
            sm: 'none',
          },
          width: '100%',
          position: 'fixed',
          justifyContent: 'space-evenly',
          bottom: 0,
          left: 0,
          zIndex: 3,
        }}
      >
        <SpeedDial
          ariaLabel="SpeedDial basic example"
          sx={{ position: 'absolute', bottom: 25, left: '50%', transform: 'translate(-50%, 0)' }}
          icon={<SpeedDialIcon />}
        >
          <SpeedDialAction
            icon={<PlayArrow />}
            slotProps={{ tooltip: { open: true, title: 'Entrada' } }}
            onClick={async () => {
              markAction(PresenceControlActions.Entrada)
            }}
          />
          {tiposEntradaSalida ? (
            tiposEntradaSalida.tipos_salida?.map((tipo, idx) => {
              const Icon = tiposSalidaMap[tipo.codigo] || tiposSalidaMap.DEFAULT
              return (
                <SpeedDialAction
                  key={idx}
                  icon={<Icon />}
                  sx={{
                    '& .MuiSpeedDialAction-staticTooltipLabel': {
                      whiteSpace: 'nowrap',
                    },
                  }}
                  slotProps={{ tooltip: { open: true, title: tipo.descripcion } }}
                  onClick={async () => {
                    markAction(PresenceControlActions.Salida, tipo.codigo)
                  }}
                />
              )
            })
          ) : (
            <SpeedDialAction
              icon={<Stop />}
              slotProps={{ tooltip: { open: true, title: 'Salida' } }}
              onClick={async () => {
                markAction(PresenceControlActions.Salida)
              }}
            />
          )}
        </SpeedDial>
        <BottomNavigationAction
          sx={{ width: '100%' }}
          label="Mapa"
          value={MovilView.Map}
          icon={<Map />}
        />
        <BottomNavigationAction
          sx={{ width: '100%' }}
          label="Fichajes"
          value={MovilView.Records}
          icon={<AlarmIcon />}
        />
      </BottomNavigation>
    </>
  )
}

export default Fichajes
